import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {FilterItem, GroupResult, Lavorazione, Ordine, OrdineCreate, OrdineEsolver, OrdineGroup, OrdiniStat, SearchResult} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {OrganizzazioniService} from './organizzazioni.service';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {SaveMessage} from '../_helpers/save-message';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {OrdineStoria} from '../_models/OrdineStoria';
import {OrdineEsolverGroup} from '../_models/OrdineEsolverGroup';
import {OrdiniShowColumns} from '../_models/OrdiniShowColumns';
import {GroupEsolverResult} from '../_models/GroupEsolverResult';
import {AnagraficaBase} from '../_models/AnagraficaBase';
import {SitRigaOrd} from '../_models/SitRigaOrd';
import {AnagraficaIndirizzo} from '../_models/AnagraficaIndirizzo';
import {Allegato} from '../_models/Allegato';
import {StatoEnum} from '../_models/generated/stato-enum'
import {AuthenticationService} from './authentication.service';
import {shouldShowNotificationForActor} from '../_helpers/notification-actor.helper';

const cachePrefix = 'archiconnect-v2-';
const emptyOrdine = {
  organizzazioneId: 0,
  idDocumento: 0,
  idRiga: 0,
  idRigaLavorazione: 0,
  risorsaId: 0,
  lavorazioneId: 0,
  quantita: 0,
  lotto: 1,
  quantitaLavorata: 0,
  quantitaScartata: 0,
  stato: StatoEnum.DA_PIANIFICARE,
  flgTerminato: false,
  statoElimina: 0,
  dataTermine: undefined,
  note: '',
  commessa: '',
  udM: '',
  isLoading: true
};
const emptyOrdineEsolver = {
  dbgruppo: '',
  idDocumento: 0,
  dataRegistrazione: new Date(),
  numRegistrazione: 0,
  idRiga: 0,
  codiceArticolo: '',
  descArt: '',
  idRigaLavorazione: 0,
  lavorazione: '',
  desLavorazione: '',
  stato: StatoEnum.DA_PIANIFICARE,
  quantita: 0,
  risorsa: '',
  reparto: '',
  desRisorsa: '',
  dataTermine: new Date(),
  hasErrors: false,
  hasWarnings: false,
  saldato: false,
  //rifOrdine: '',
  quantitaLavorata: 0,
  quantitaScartata: 0,
  quantitaNonConforme: 0,
  tempo: '',
  qtaOrdine: 0,
  commessa: '',
  rifLottoAlfanum: '',
  udM: '',
  numFase: 1
};
const baseShowColumns = {
  rifOrdine: false,
  numRegistrazione: true,
  rifOdP: true,
  dataTermine: true,
  risorsa: true,
  lavorazione: true,
  codiceArticolo: true,
  quantita: true,
  stato: true,
  numFase: false,
  rifLottoProdotto: false,
  rifLottoMateriale: false,
  udM: false,
  codCliente: false,
  idOrdVen: false,
  codTerzista: false
};

@Injectable({providedIn: 'root'})
export class LocalService {
  public handleRefresh = new Subject();
  public handleSave: Subject<number> = new Subject();
  public localFilters = new BehaviorSubject<FilterItem[]>([]);

  public searchOrdineGroup: BehaviorSubject<OrdineGroup> = new BehaviorSubject<OrdineGroup>(emptyOrdine);
  public searchStato: BehaviorSubject<number> = new BehaviorSubject(-1);
  public searchDataDa: BehaviorSubject<string> = new BehaviorSubject('');
  public searchDataA: BehaviorSubject<string> = new BehaviorSubject('');
  public searchText: BehaviorSubject<string> = new BehaviorSubject('');
  public key: BehaviorSubject<string> = new BehaviorSubject('');
  public sortDirection: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public showColumns = new BehaviorSubject<OrdiniShowColumns>(baseShowColumns);

  public lastOrdineLaunched: Subject<Ordine> = new Subject<Ordine>();
  public lastOrdineCreated: BehaviorSubject<Ordine> = new BehaviorSubject<Ordine>(null);
  public lastOrdineUpdated: BehaviorSubject<Ordine> = new BehaviorSubject<Ordine>(null);
  public lastOrdineDeleted: BehaviorSubject<Ordine> = new BehaviorSubject<Ordine>(null);
  public pendingOrdineDeletes: BehaviorSubject<Ordine[]> = new BehaviorSubject<Ordine[]>([]);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  private hubConnection: HubConnection;
  private currentOrdineGroup: OrdineGroup;
  private ordineGroupRequestId = 0;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {
    const ordineGroup = JSON.parse(localStorage.getItem(cachePrefix + 'ordineGroup')) || emptyOrdine;
    this.searchOrdineGroup.next(ordineGroup);
    this.currentOrdineGroup = ordineGroup;
    const ordiniShowColumns = JSON.parse(localStorage.getItem(cachePrefix + 'ordiniShowColumns')) || baseShowColumns;
    this.showColumns.next(ordiniShowColumns);
  }

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/ordine`, this.hubState);

    this.hubConnection.on('SendCreated', (ordine: Ordine) => {
      if (ordine.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityCreated('ordine', ordine);
        this.lastOrdineCreated.next(ordine);
      }
    });

    this.hubConnection.on('SendUpdated', (ordine: Ordine) => {
      if (ordine.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        if (this.shouldShowUpdateNotification(ordine.executedBy)) {
          this.saveMessage.entityUpdated('ordine', ordine);
        }
        this.lastOrdineUpdated.next(ordine);
      }
    });

    this.hubConnection.on('SendDeleted', (ordine: Ordine) => {
      if (ordine.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.clearOrdineDeletePending(ordine);
        this.saveMessage.entityDeleted('ordine', ordine);
        this.lastOrdineDeleted.next(ordine);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  markOrdineDeletePending(ordine: Ordine): void {
    if (!ordine || this.pendingOrdineDeletes.value.some((_pending) => this.isSameOrdine(_pending, ordine))) {
      return;
    }
    this.pendingOrdineDeletes.next([...this.pendingOrdineDeletes.value, ordine]);
  }

  clearOrdineDeletePending(ordine: Ordine): void {
    const pending = this.pendingOrdineDeletes.value;
    const remaining = pending.filter((_pending) => !this.isSameOrdine(_pending, ordine));
    if (remaining.length !== pending.length) {
      this.pendingOrdineDeletes.next(remaining);
    }
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  private isSameOrdine(left: Ordine, right: Ordine): boolean {
    return left?.organizzazioneId === right?.organizzazioneId &&
      left?.idDocumento === right?.idDocumento &&
      left?.idRiga === right?.idRiga &&
      left?.idRigaLavorazione === right?.idRigaLavorazione;
  }

  getEmptyOrder = () => ({...emptyOrdine} as OrdineGroup);
  getEmptyOrderEsolver = () => ({
    ...emptyOrdineEsolver,
    dataRegistrazione: new Date(),
    dataTermine: new Date()
  } as OrdineEsolverGroup);

  async getOrdineWithTimeInfo(ordine: Ordine) {
    return this.getOrdiniTempoAsync(ordine.organizzazioneId, ordine.idDocumento, ordine.idRiga, ordine.idRigaLavorazione)
      .then(async (_ordineTempo: Ordine) => {
        ordine = _ordineTempo;
        this.setOrdineFromEsolver(ordine);
      });
  }

  setOrdineFromEsolver(ordine: Ordine) {
    if (this.currentOrdineGroup.ordini == null) {
      return false;
    }
    for (let i = 0; i < this.currentOrdineGroup.ordini.length; i++) {
      if (this.currentOrdineGroup.ordini[i].idDocumento === ordine.idDocumento &&
        this.currentOrdineGroup.ordini[i].idRiga === ordine.idRiga &&
        this.currentOrdineGroup.ordini[i].idRigaLavorazione === ordine.idRigaLavorazione) {
        this.currentOrdineGroup.ordini[i] = ordine;
      }
    }
    this.calculateOrdineGroup();
  }

  async setSumOrdineGroup(ordineGroup: OrdineGroup, getOrdiniTimeInfo?: boolean) {
    const requestId = ++this.ordineGroupRequestId;
    this.currentOrdineGroup = ordineGroup;
    if (this.currentOrdineGroup.ordini == null || this.currentOrdineGroup.ordini.length === 0) {
      return false;
    }
    if (getOrdiniTimeInfo) {
      this.currentOrdineGroup.isLoading = true;
      try {
        const ordineGroupUpdated = await this.getOrdiniGroupTempoAsync(ordineGroup);
        if (requestId !== this.ordineGroupRequestId) {
          return false;
        }
        ordineGroupUpdated.isLoading = false;
        this.currentOrdineGroup = ordineGroupUpdated;
        this.calculateOrdineGroup();
        return true;
      } catch {
        return false;
      } finally {
        if (requestId === this.ordineGroupRequestId) {
          this.currentOrdineGroup.isLoading = false;
        } else {
          ordineGroup.isLoading = false;
        }
      }
    }

    if (requestId !== this.ordineGroupRequestId) {
      return false;
    }
    this.calculateOrdineGroup();
    return true;
  }

  calculateOrdineGroup() {
    if (this.currentOrdineGroup?.ordini == null || this.currentOrdineGroup.ordini.length === 0) {
      return false;
    }
    this.currentOrdineGroup.quantita = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantita, 0);
    this.currentOrdineGroup.quantitaOrdinataUdM2 = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaOrdinataUdM2, 0);
    this.currentOrdineGroup.quantitaLavorata = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaLavorata, 0);
    this.currentOrdineGroup.quantitaScartata = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaScartata, 0);
    this.currentOrdineGroup.quantitaNonConforme = this.currentOrdineGroup.ordini
      .reduce((sum, current) => sum + (current.quantitaNonConforme ?? 0), 0) ?? 0;
    this.currentOrdineGroup.quantitaLavorataUdM2 = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaLavorataUdM2, 0);
    this.currentOrdineGroup.quantitaScartataUdM2 = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaScartataUdM2, 0);
    this.currentOrdineGroup.quantitaNonConformeUdM2 = this.currentOrdineGroup.ordini
      .reduce((sum, current) => sum + (current.quantitaNonConformeUdM2 ?? 0), 0) ?? 0;
    this.currentOrdineGroup.qtaRimanente = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.qtaRimanente, 0);
    this.currentOrdineGroup.lastFase = this.currentOrdineGroup.ordini.reduce((sum, current) => sum + current.lastFase, 0);
    this.currentOrdineGroup.totalWorkingHours = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.totalWorkingHours, 0);
    this.currentOrdineGroup.workingTime = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.workingTime, 0);
    this.currentOrdineGroup.partialWorkingHours = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.partialWorkingHours, 0);
    this.currentOrdineGroup.countWorkingSessions = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.countWorkingSessions, 0);
    this.currentOrdineGroup.quantitaLavorataWait = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaLavorataWait, 0);
    this.currentOrdineGroup.quantitaScartataWait = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaScartataWait, 0);
    this.currentOrdineGroup.quantitaNonConformeWait = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.quantitaNonConformeWait, 0);
    this.currentOrdineGroup.tempoLavWait = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + current.tempoLavWait, 0);
    this.currentOrdineGroup.tempoLav = this
      .currentOrdineGroup.ordini.reduce((sum, current) => sum + parseFloat(current.tempoLav), 0).toString();
    this.currentOrdineGroup.flgTerminato = this.currentOrdineGroup.ordini.every(({flgTerminato}) => flgTerminato);
    this.currentOrdineGroup.erpFound = this.currentOrdineGroup.ordini.every(({erpFound}) => erpFound);
    this.currentOrdineGroup.workStartTime = this.currentOrdineGroup.ordini[0].workStartTime;
    // Il gruppo è in avanzamento se lo è almeno un ordine, anche senza ora di avvio: altrimenti i pulsanti
    // lo mostrerebbero fermo. L'inizio della sessione viene solo dalle ore di avvio note degli ordini attivi,
    // mai dalla data di un ordine in pausa.
    const attivi = this.currentOrdineGroup.ordini.filter((ordine) => ordine.stato === StatoEnum.AVANZAMENTO);
    const avvii = attivi
      .map((ordine) => ordine.lastActionTime)
      .filter((avvio): avvio is number => avvio != null);
    if (attivi.length === 0) {
      this.currentOrdineGroup.lastActionTime = this.currentOrdineGroup.ordini[0].lastActionTime;
    } else {
      this.currentOrdineGroup.lastActionTime = avvii.length > 0 ? Math.min(...avvii) : undefined;
    }
    // Le date mancanti arrivano come undefined (l'API omette i null): non entrano nel massimo.
    const eventi = this.currentOrdineGroup.ordini
      .map((ordine) => ordine.lastEventoTime)
      .filter((evento): evento is number => evento != null);
    this.currentOrdineGroup.lastEventoTime = eventi.length > 0 ? Math.max(...eventi) : undefined;
    this.currentOrdineGroup.stato = attivi.length > 0
      ? StatoEnum.AVANZAMENTO
      : this.currentOrdineGroup.ordini[0].stato;
    this.setOrdineGroup(this.currentOrdineGroup);
    return true;
  }

  setOrdineGroup(ordineGroup: OrdineGroup, onlyStorageUpdate?: boolean) {
    localStorage.setItem(cachePrefix + 'ordineGroup', JSON.stringify(ordineGroup));
    if (!onlyStorageUpdate) {
      this.searchOrdineGroup.next(ordineGroup);
      this.currentOrdineGroup = ordineGroup;
    }
  }

  setOrdiniShowColumns(showColumns: OrdiniShowColumns, onlyStorageUpdate?: boolean) {
    localStorage.setItem(cachePrefix + 'ordiniShowColumns', JSON.stringify(showColumns));
    if (!onlyStorageUpdate) {
      this.showColumns.next(showColumns);
    }
  }

  resetOrdineGroup(onlyStorageUpdate?: boolean, isLoading?: boolean) {
    this.ordineGroupRequestId++;
    const copyOrdine = {...emptyOrdine, isLoading};
    this.currentOrdineGroup = copyOrdine;
    localStorage.setItem(cachePrefix + 'ordineGroup', JSON.stringify(copyOrdine));
    if (!onlyStorageUpdate) {
      this.searchOrdineGroup.next(copyOrdine);
    }
  }

  getOrdiniInviatiFilteredGroupByMany(
    orgId: number,
    terminato: boolean,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    filters: FilterItem[],
    groupColumns: string[]) {
    return firstValueFrom(
      this.http.post<SearchResult<GroupResult<Ordine>>>(`${environment.localApiUrl}api/Ordini/MyOrdiniInviatiGroupByMany`,
        {
          orgId, terminato, search, page, count, sortField, sortAsc,
          filtroOrdini: this.organizzazioniService.filtroOrdini, filters, groupColumns
        })
    );
  }

  getOrdiniInviatiFiltered(
    orgId: number,
    terminato: boolean,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    filters: FilterItem[],
    personal: boolean) {
    const method = personal ? `MyOrdiniInviati` : `OrdiniInviati`;
    return firstValueFrom(this.http.post<SearchResult<Ordine>>(`${environment.localApiUrl}api/Ordini/${method}`,
      {
        orgId, terminato, search, page, count, sortField,
        filtroOrdini: this.organizzazioniService.filtroOrdini, sortAsc, filters
      }));
  }

  getOrdiniEsolver(
    orgId: number,
    group: string,
    connectionName: string,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    saldato: boolean,
    isPersonal: boolean,
    contoLavoro: boolean,
    filters: FilterItem[],
    noKeyFilter: boolean) {
    return firstValueFrom(this.http.post<SearchResult<OrdineEsolver>>(`${environment.localApiUrl}api/Esolver/Ordini`,
      {
        orgId, group, connectionName, search, page, count, sortField, sortAsc,
        filtroOrdini: !isPersonal ? 0 : this.organizzazioniService.filtroOrdini, saldato, contoLavoro, filters, noKeyFilter
      }));
  }

  getOrdiniGroupEsolver(
    orgId: number,
    group: string,
    connectionName: string,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    saldato: boolean,
    isPersonal: boolean,
    contoLavoro: boolean,
    filters: FilterItem[],
    groupColumns: string[]) {
    return firstValueFrom(this.http.post<SearchResult<OrdineEsolverGroup>>(`${environment.localApiUrl}api/Esolver/OrdiniGroup`,
      {
        orgId, group, connectionName, search, page, count, sortField, sortAsc,
        filtroOrdini: !isPersonal ? 0 : this.organizzazioniService.filtroOrdini, saldato, contoLavoro, filters, groupColumns
      }));
  }

  getOrdiniGroupByManyEsolver(
    orgId: number,
    group: string,
    connectionName: string,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    saldato: boolean,
    isPersonal: boolean,
    filters: FilterItem[],
    groupColumns: string[]) {
    return firstValueFrom(this.http.post<SearchResult<GroupEsolverResult<OrdineEsolver>>>(`${environment.localApiUrl}api/Esolver/OrdiniGroupByMany`,
      {
        orgId, group, connectionName, search, page, count, sortField, sortAsc,
        filtroOrdini: !isPersonal ? 0 : this.organizzazioniService.filtroOrdini, saldato, filters, groupColumns
      }));
  }

  async getMaterialeOfOrdineGroup(ordineGroup: OrdineGroup) {
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Esolver/MaterialeOfOrdineGroup`, ordineGroup));
  }

  async getMateriale() {
    try {
      this.currentOrdineGroup = await this.getMaterialeOfOrdineGroup(this.searchOrdineGroup.value);
      this.calculateOrdineGroup();
    } catch {
      return this.saveMessage.messageError("Ci sono problemi con il caricamento del materiale.");
    }
  }

  async createOrdine(ordine: OrdineCreate) {
    ordine.executedBy ??= this.getNotificationActor();
    return firstValueFrom(
      this.http.post<Ordine>(`${environment.localApiUrl}api/Ordini/CreaOrdineWithCode`, ordine)
    );
  }

  async updateOrdine(ordine: Ordine) {
    ordine.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Ordine>(`${environment.localApiUrl}api/Ordini/UpdateOrdine`, ordine));
  }

  // Feature 105: aggiornamento sincrono dei soli campi utente dell'ordine (validazione strict
  // lato backend: 400 { errorMessage } se un valore non rispetta la definizione).
  async updateOrdineCampiUtente(request: {
    orgId: number;
    idDocumento: number;
    idRiga: number;
    idRigaLavorazione: number;
    campiUtente: string | null;
  }) {
    return firstValueFrom(
      this.http.post(`${environment.localApiUrl}api/Ordini/UpdateOrdineCampiUtente`, request)
    );
  }

  async updloadFileOrdineAsync(idDocumento: number, idRiga: number, idRigaLavorazione: number, orgId: number, files: any) {
    if (files.length === 0) {
      return;
    }
    const fileToUpload = files[0] as File;
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    return firstValueFrom(this.http.post(`${environment.localApiUrl}api/Ordini/Upload/${idDocumento}/${idRiga}/${idRigaLavorazione}/${orgId}`,
      formData));
  }

  getOrdiniGroupTempoAsync(ordineGroup: OrdineGroup) {
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Ordini/CalcoloTempoOrdineGroup`, ordineGroup));
  }

  getOrdiniTempoAsync(orgId: number, idDocumento: number, idRiga: number, idRigaLavorazione: number) {
    return firstValueFrom(this.http.get<Ordine>(`${environment.localApiUrl}api/Ordini/CalcoloTempoOrdine?orgId=${orgId}&idDocumento=${idDocumento}&idRiga=${idRiga}&idRigaLavorazione=${idRigaLavorazione}`));
  }

  getOrdineAsync(orgId: number, idDocumento: number, idRiga: number, idRigaLavorazione: number) {
    return firstValueFrom(this.http.get<Ordine>(`${environment.localApiUrl}api/Ordini/Ordine?orgId=${orgId}&idDocumento=${idDocumento}&idRiga=${idRiga}&idRigaLavorazione=${idRigaLavorazione}`));
  }

  getOrdineFasiByRifOdP(orgId: number, rifOdP: string) {
    return firstValueFrom(this.http.get<SearchResult<Ordine>>(`${environment.localApiUrl}api/Ordini/OrdineFasiByRifOdP?orgId=${orgId}&rifOdP=${rifOdP}`));
  }

  // Storia Ordine di Produzione (issue #3163). idRiga assente ⇒ ambito intero documento.
  getOrdineStoriaAsync(orgId: number, idDocumento: number, idRiga?: number) {
    const rigaQuery = idRiga != null ? `&idRiga=${idRiga}` : '';
    return firstValueFrom(this.http.get<OrdineStoria>(`${environment.localApiUrl}api/Ordini/Storia?orgId=${orgId}&idDocumento=${idDocumento}${rigaQuery}`));
  }

  deleteOrdineAsync(orgId: number, idDocumento: number, idRiga: number, idRigaLavorazione: number) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `&executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<Ordine>(`${environment.localApiUrl}api/Ordini/DeleteOrdine?orgId=${orgId}&idDocumento=${idDocumento}&idRiga=${idRiga}&idRigaLavorazione=${idRigaLavorazione}${actorQuery}`));
  }

  getStatistics(orgId: number) {
    return firstValueFrom(this.http.get<OrdiniStat>(`${environment.localApiUrl}api/Ordini/GetOrdiniStatistiche?orgId=${orgId}`));
  }

  async getAnagrafiche(orgId: number, tipo: number, terzista: boolean, search: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<AnagraficaBase>>(`${environment.localApiUrl}api/Esolver/Anagrafiche/${orgId}/${tipo}`,
      {anagTerzista: terzista, search, page, count}));
  }

  async getIndirizziOfAnagrafiche(orgId: number, codCliFor: number, tipo: number) {
    return firstValueFrom(this.http.get<SearchResult<AnagraficaIndirizzo>>(`${environment.localApiUrl}api/Esolver/IndirizziOfAnagrafica/${orgId}/${codCliFor}/${tipo}`));
  }

  async getFornitoriOrdiniApertiAsync(orgId: number, gruppoDoc: string, groupDocOriginale: number, search: string) {
    return firstValueFrom(this.http.post<SearchResult<AnagraficaBase>>(`${environment.localApiUrl}api/Esolver/FornitoriOrdiniAperti`,
      {orgId, gruppoDoc, groupDocOriginale, search}));
  }

  async getRigheOrdiniApertiByFornitoreAsync(orgId: number, idAnagrafica: number, rifDoc: string, gruppoDoc: string) {
    return firstValueFrom(this.http.post<SearchResult<SitRigaOrd>>(`${environment.localApiUrl}api/Esolver/RigheOrdiniApertiByFornitore`,
      {orgId, idAnagrafica, rifDoc, gruppoDoc}));
  }

  async getTerzistiOrdiniApertiAsync(orgId: number, codLav: string) {
    return firstValueFrom(this.http.get<SearchResult<AnagraficaBase>>(`${environment.localApiUrl}api/Esolver/TerzistiOrdiniAperti/${orgId}?codLav=${codLav}`));
  }

  async getContoLavoroApertiAsync(orgId: number) {
    return firstValueFrom(this.http.get<SearchResult<Lavorazione>>(`${environment.localApiUrl}api/Esolver/ContoLavoroAperti/${orgId}`));
  }

  async getOrdiniTerzisitByRifOdPAsync(orgId: number, rifOdP: string, codArt?: string) {
    return firstValueFrom(this.http.post<SearchResult<OrdineEsolver>>(`${environment.localApiUrl}api/Esolver/OrdiniTerzistiByRifOdP/${orgId}`,
      {search: rifOdP, codArt, page: 0, count: 0}));
  }

  async getOrdiniContoLavoroByRifOdP(orgId: number, rifOdP: string, codArt?: string, codLotto?: string) {
    // codArt/codLotto viaggiano nel body (OrdiniContoLavoroRequest : SearchPaged) — Constitution III, no [FromQuery]
    return firstValueFrom(this.http.post<SearchResult<OrdineEsolver>>(`${environment.localApiUrl}api/Esolver/OrdiniContoLavoroByRifOdP/${orgId}`,
      {search: rifOdP, page: 0, count: 0, codArt, codLotto}));
  }

  async getAllegatiOrdineByRifOdPAsync(orgId: number, rifOdP: string) {
    return firstValueFrom(this.http.post<SearchResult<Allegato>>(`${environment.localApiUrl}api/Esolver/Allegati/${orgId}`,
      {search: rifOdP, page: 0, count: 0}));
  }

  async getAllegatoOrdineAsync(orgId: number, path: string, fileName: string) {
    return firstValueFrom(this.http.post<any>(`${environment.localApiUrl}api/Esolver/Allegato/${orgId}`,
      {path: path, fileName: fileName}, {responseType: 'blob' as 'json', observe: 'response'}));
  }

  padLeft(num: number, size: number) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  private getNotificationActor(): string | undefined {
    return this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue?.userName;
  }

  private shouldShowUpdateNotification(executedBy?: string): boolean {
    return shouldShowNotificationForActor(executedBy, [this.organizzazioniService.codiceOperatore, this.authService.currentUserValue?.userName]);
  }
}
